import { useCallback, useMemo, useState } from 'react';
import {
  resolveAllocationDocumentEntry,
  type AllocationDocumentEntryState,
  type AllocationDocumentRow
} from '../utils/document-entry';
import { useInvoiceAllocationDetails, useSearchDialog, type ReceiptRow } from '../hooks';
import { buildDynamicFilters, type DynamicFilters } from '@/lib/dynamic-filters';
import { type GridRowParams, type SearchValues } from '@/components/custom/erp';
import { getReceiptColumns } from '../cols-definition';
import type { SearchFormValues } from '../schema';
import { QUERY_KEYS } from '@/constants';
import { useReport } from '@/hooks';

export const usePhanBoThuTienTable = () => {
  const searchDialog = useSearchDialog();
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptRow | null>(null);
  const invoiceDetails = useInvoiceAllocationDetails(selectedReceipt);
  const [documentEntry, setDocumentEntry] = useState<AllocationDocumentEntryState | null>(null);
  const { data, isLoading, totalItems, currentPage, handlePageChange, getReport } = useReport<
    ReceiptRow,
    SearchFormValues & { dynamic_filters?: DynamicFilters }
  >({
    endpoint: QUERY_KEYS.PHAN_BO_THU_TIEN_CHO_CAC_HOA_DON,
    pageSize: 20,
    prefetch: false
  });

  const handleSearch = useCallback(
    async (values: SearchFormValues) => {
      setSelectedReceipt(null);
      await getReport(values);
      searchDialog.onSubmit(values);
    },
    [getReport, searchDialog]
  );

  const handleRowClick = useCallback((params: GridRowParams) => {
    setSelectedReceipt(params.row as ReceiptRow);
  }, []);

  const handleDocumentClick = useCallback((row: AllocationDocumentRow) => {
    const nextDocumentEntry = resolveAllocationDocumentEntry(row);
    if (nextDocumentEntry) setDocumentEntry(nextDocumentEntry);
  }, []);

  const receiptColumns = useMemo(() => getReceiptColumns(handleDocumentClick), [handleDocumentClick]);

  const handleDynamicFilter = useCallback(
    (values: SearchValues) => {
      if (!searchDialog.hasSearched) return;

      const filterInputs = Object.fromEntries(
        Object.entries(values).filter(([, value]) => typeof value === 'string' || typeof value === 'number')
      ) as Record<string, string | number>;
      const dynamicFilters = buildDynamicFilters(filterInputs);

      void getReport({
        ...searchDialog.searchParams,
        ...(Object.keys(dynamicFilters).length > 0 ? { dynamic_filters: dynamicFilters } : {})
      });
    },
    [getReport, searchDialog.hasSearched, searchDialog.searchParams]
  );

  const tables = useMemo(
    () => [
      {
        name: '',
        rows: searchDialog.hasSearched ? data.map(row => ({ ...row, uuid: row.document_uuid || row.uuid })) : [],
        columns: receiptColumns
      }
    ],
    [data, receiptColumns, searchDialog.hasSearched]
  );

  return {
    searchDialog: { ...searchDialog, onSubmit: handleSearch },
    documentEntry: {
      value: documentEntry,
      onClose: () => setDocumentEntry(null)
    },
    selection: { selectedReceipt },
    table: {
      tables,
      displayIsLoading: isLoading,
      onRowClick: handleRowClick,
      totalItems,
      currentPage,
      onPageChange: handlePageChange,
      onSearchChange: handleDynamicFilter
    },
    detailTable: {
      rows: invoiceDetails.rows,
      isLoading: invoiceDetails.isLoading,
      refresh: invoiceDetails.refresh,
      toggleSelection: invoiceDetails.toggleSelection,
      onDocumentClick: handleDocumentClick
    }
  };
};
